import { useMutation } from '@tanstack/react-query';
import { UpdateConfigPayload } from 'src/components/modal/screenConfigModal/ScreenConfigModal.interface';
import { queryClient } from 'src/components/queryProvider';
import { COLLECTIONS } from 'src/constants/constants';
import { ILayoutConfigData } from 'src/interfaces';
import { lang, queryKeys, requestCCApi } from 'src/utils';

import { generateSingleConfigString, getScreenId } from './helpers';

const updateLayout = async (
  payload: UpdateConfigPayload,
  updateType: 'config' | 'name' | 'rotation',
) => {
  const { layout, id, index, name, status } = payload;

  const currentScreen = layout?.screens[index] ?? {
    name: '',
    id: '',
    width: '',
    height: '',
    layoutScreenId: '',
  };

  const isPlaceholder = currentScreen?.name?.includes('placeholder');
  let updateData: Record<string, any> = {};

  if (updateType === 'config') {
    const newGridLines = payload?.layout?.config?.gridLines ?? [];
    newGridLines[payload.index] = generateSingleConfigString(payload);

    if (!isPlaceholder) {
      const orientation =
        currentScreen.height > currentScreen.width ? 'Vertical' : 'Horizontal';
      const placeholderScreenId = await getScreenId(orientation);

      await requestCCApi({
        url: `/transaction`,
        method: 'POST',
        data: {
          data: [
            {
              resource: COLLECTIONS.LAYOUT_SCREENS,
              id: Number(currentScreen.layoutScreenId),
              method: 'DELETE',
            },
            {
              resource: COLLECTIONS.SCREENS,
              id: Number(currentScreen.id),
              method: 'DELETE',
            },
            {
              resource: COLLECTIONS.LAYOUT_SCREENS,
              method: 'POST',
              data: {
                layout_id: Number(id),
                channel_screen_sequence: index + 1,
                screen_id: placeholderScreenId,
              },
            },
          ],
        },
      });
    }

    const updatedConfig: ILayoutConfigData = {
      ...layout?.config,
      gridLines: newGridLines,
      rotationGridLines: newGridLines,
    };

    updateData = {
      name,
      config: updatedConfig,
    };
  } else if (updateType === 'name') {
    updateData = { name, status };
  } else if (updateType === 'rotation') {
    let newGridLines = payload?.layout?.config?.rotationGridLines ?? [];
    newGridLines[payload.index] = generateSingleConfigString(payload);
    const updatedConfig: ILayoutConfigData = {
      ...layout?.config,
      rotationGridLines: newGridLines,
    };

    updateData = {
      name,
      config: updatedConfig,
    };
  }

  return requestCCApi(
    {
      url: `/layouts/${id}`,
      method: 'PATCH',
      data: { data: updateData },
    },
    true,
    lang.addLayoutsPage.layoutExistsError,
  );
};

export const useUpdateLayout = (updateType: 'config' | 'name' | 'rotation') => {
  return useMutation({
    mutationFn: (payload: UpdateConfigPayload) =>
      updateLayout(payload, updateType),
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.layoutScreens.invalidateAll());
      queryClient.invalidateQueries(queryKeys.layouts.invalidateAll());
      queryClient.invalidateQueries(queryKeys.screenSections.invalidateAll());
    },
  });
};
